// The asset widget (section 6.4): a combobox grouped by kind with thumbnails for images, None, and Upload… which posts
// the bytes at once (an asset is its own resource) and then points the field at the new id.
import { useAssetUrls } from '../../api/assetUrls';
import { useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Parameter } from '../../api/types';
import { Button } from '../../ui/Button';
import { Combobox } from '../../ui/Combobox';
import { Image, Upload } from '../../ui/icons';
import { useToasts } from '../../ui/Toast';
import { useWidgetEnv, type WidgetProps } from './context';

const KIND_BY_MIME: [RegExp, string][] = [
  [/^image\//, 'image'],
  [/^audio\//, 'audio'],
  [/^font\/|opentype|truetype|woff/, 'font'],
  [/json/, 'json'],
  [/javascript/, 'javascript'],
];

/**
 * The upload behind "Upload…" and the drop zones (AssetDropField): posts the bytes at once (an asset is its own resource),
 * refreshes the catalog and answers the new asset's id, or null after a toasted failure.
 */
export function useAssetUpload(p: Pick<Parameter, 'assetKinds'>): { upload: (file: File) => Promise<string | null>; uploading: boolean } {
  const { catalog, client, org, game, branch } = useWidgetEnv();
  const toasts = useToasts();
  const [uploading, setUploading] = useState(false);
  const kinds = p.assetKinds ?? [];
  const upload = async (file: File): Promise<string | null> => {
    setUploading(true);
    try {
      const kind = kinds.length === 1 ? kinds[0]! : (KIND_BY_MIME.find(([re]) => re.test(file.type))?.[1] ?? (kinds[0] ?? 'other'));
      const dot = file.name.lastIndexOf('.');
      const extension = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : undefined;
      const result = await client.call(ops.uploadAsset, { path: { org, game, branch }, query: { name: file.name, kind, extension }, rawBody: file, contentType: file.type || 'application/octet-stream' });
      const asset = result.data as { id: string };
      catalog.refresh();
      toasts.success(`${file.name} uploaded.`);
      return asset.id;
    } catch (e) {
      toasts.error(e instanceof ApiError ? e.message : String(e), { requestId: e instanceof ApiError ? e.requestId : undefined });
      return null;
    } finally {
      setUploading(false);
    }
  };
  return { upload, uploading };
}

export function AssetPicker({ param: p, value, onCommit, disabled, compact, id }: WidgetProps) {
  const { catalog, client, org, game, branch } = useWidgetEnv();
  const fileInput = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useAssetUpload(p);
  const kinds = p.assetKinds ?? [];
  const thumbs = useAssetUrls(
    { client, org, game, branch },
    catalog.assetList.filter((a) => a.kind === 'image' && a.contentPath && (kinds.length === 0 || kinds.includes(a.kind))).map((a) => a.id),
  );
  const options = useMemo(
    () =>
      catalog.assetList
        .filter((a) => kinds.length === 0 || kinds.includes(a.kind))
        .sort((a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name))
        .map((a) => ({ value: a.id, label: a.name, hint: `${a.kind}${a.group ? ` · ${a.group}` : ''}`, icon: a.kind === 'image' && a.contentPath && thumbs[a.id] ? <img className="asset-thumb" src={thumbs[a.id]} alt="" /> : undefined })),
    [catalog.assetList, kinds, thumbs],
  );
  const current = typeof value === 'string' ? value : null;
  const currentAsset = current ? catalog.assets.get(current) : undefined;
  const currentThumb = current && currentAsset?.kind === 'image' ? thumbs[current] : undefined;
  const take = async (file: File | undefined) => {
    if (!file) return;
    const id = await upload(file);
    if (id) onCommit(id);
  };
  return (
    <span className="row asset-picker">
      {!compact && current && (currentThumb ? <img className="thumb" src={currentThumb} alt="" /> : <span className="thumb placeholder" aria-hidden="true"><Image className="icon-s" /></span>)}
      <Combobox id={id} size={compact ? 's' : 'm'} value={current} options={options} onChange={(v) => onCommit(v)} emptyLabel="None" placeholder={kinds.length ? `An ${kinds.join(' / ')} asset…` : 'An asset…'} disabled={disabled} aria-label={p.displayName ?? p.name} />
      {!disabled && !compact && (
        <>
          <input ref={fileInput} type="file" hidden onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ''; }} aria-label={`Upload for ${p.displayName ?? p.name}`} />
          <Button variant="ghost" onClick={() => fileInput.current?.click()} busy={uploading} icon={<Upload aria-hidden="true" />}>
            Upload…
          </Button>
        </>
      )}
    </span>
  );
}
